import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import NavigationBar from '@/components/NavigationBar';
import Footer from '@/components/Footer';
import { ChevronRight, ExternalLink, Filter, ArrowUpDown } from 'lucide-react';
import { getAssets } from '../utils/api';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { useCartCount } from '@/hooks/useCartCount';
import ImageWithLoader from '@/components/ImageWithLoader';
import LoadingSpinner from '@/components/LoadingSpinner';

// NO MORE HARDCODED DATA - fetching from API
const REMOVED_mockLuxuryCars = [
  {
    id: 'lam-aventador-svj',
    name: 'REMOVED - Lamborghini Aventador SVJ',
    description: 'Experience the pinnacle of Italian automotive excellence. This limited edition Aventador SVJ delivers unparalleled performance with its naturally aspirated V12 engine, producing 770 horsepower. Every drive is an unforgettable experience.',
    images: [
      'https://images.unsplash.com/photo-1621135802920-133df287f89c?crop=entropy&cs=srgb&fm=jpg&q=85',
      'https://images.unsplash.com/photo-1544829099-b9a0c07fad1a?crop=entropy&cs=srgb&fm=jpg&q=85'
    ],
    price_per_ticket: 2500000,
    total_tickets: 12,
    sold_tickets: 3,
    type: 'Coupe',
    cities: ['Mumbai', 'Delhi', 'Bangalore'],
    features: ['V12 Engine - 770 HP', 'Carbon Fiber Monocoque', 'Active Aerodynamics', 'All-Wheel Drive']
  },
  {
    id: 'ferrari-sf90',
    name: 'Ferrari SF90 Stradale',
    description: "Ferrari's first series-production plug-in hybrid supercar. Combining a twin-turbo V8 with three electric motors, the SF90 delivers 986 horsepower. Experience the future of performance with Italian craftsmanship.",
    images: [
      'https://images.unsplash.com/photo-1583121274602-3e2820c69888?crop=entropy&cs=srgb&fm=jpg&q=85',
      'https://images.unsplash.com/photo-1592198084033-aade902d1aae?crop=entropy&cs=srgb&fm=jpg&q=85'
    ],
    price_per_ticket: 3000000,
    total_tickets: 10,
    sold_tickets: 1,
    type: 'Coupe',
    cities: ['Mumbai', 'Bangalore', 'Hyderabad'],
    features: ['Hybrid Powertrain - 986 HP', 'E-Manettino Selector', 'Carbon Fiber Construction', 'RAC-e Traction Control']
  },
  {
    id: 'rolls-royce-phantom',
    name: 'Rolls-Royce Phantom VIII',
    description: 'The ultimate in luxury motoring. Handcrafted to perfection with a 6.75L V12 engine. Experience unparalleled comfort, refinement, and prestige. Every detail meticulously crafted for the most discerning owners.',
    images: [
      'https://images.unsplash.com/photo-1631295868223-63265b40d9e4?crop=entropy&cs=srgb&fm=jpg&q=85',
      'https://images.unsplash.com/photo-1563720360172-67b8f3dce741?crop=entropy&cs=srgb&fm=jpg&q=85'
    ],
    price_per_ticket: 4500000,
    total_tickets: 8,
    sold_tickets: 0,
    type: 'Limousine',
    cities: ['Delhi', 'Mumbai', 'Pune'],
    features: ['6.75L V12 - 563 HP', 'Magic Carpet Ride Suspension', 'Starlight Headliner', 'Bespoke Customization']
  },
  {
    id: 'mclaren-720s',
    name: 'McLaren 720S',
    description: 'British engineering at its finest. The 720S combines stunning design with blistering performance. Its carbon fiber MonoCell II chassis and twin-turbo V8 deliver an unforgettable driving experience.',
    images: [
      'https://images.unsplash.com/photo-1568605117036-5fe5e7bab0b7?crop=entropy&cs=srgb&fm=jpg&q=85',
      'https://images.unsplash.com/photo-1580273916550-e323be2ae537?crop=entropy&cs=srgb&fm=jpg&q=85'
    ],
    price_per_ticket: 2200000,
    total_tickets: 10,
    sold_tickets: 2,
    type: 'Coupe',
    cities: ['Bangalore', 'Chennai', 'Hyderabad'],
    features: ['Twin-Turbo V8 - 710 HP', 'Carbon Fiber MonoCell II', 'Proactive Chassis Control', 'Dihedral Doors']
  },
  {
    id: 'bentley-continental',
    name: 'Bentley Continental GT',
    description: 'The perfect blend of luxury and performance. The Continental GT features a handcrafted interior and a powerful W12 engine. Experience grand touring in its most refined form.',
    images: [
      'https://images.unsplash.com/photo-1549399542-7e3f8b79c341?crop=entropy&cs=srgb&fm=jpg&q=85',
      'https://images.unsplash.com/photo-1566023888232-6a0089df5bbe?crop=entropy&cs=srgb&fm=jpg&q=85'
    ],
    price_per_ticket: 2800000,
    total_tickets: 10,
    sold_tickets: 4,
    type: 'Convertible',
    cities: ['Mumbai', 'Pune', 'Ahmedabad'],
    features: ['W12 Engine - 626 HP', 'Rotating Display', 'Diamond-Quilted Leather', 'Active All-Wheel Drive']
  },
  {
    id: 'porsche-911-turbo',
    name: 'Porsche 911 Turbo S',
    description: 'The iconic 911 in its most powerful form. The Turbo S delivers legendary performance with modern technology. Experience the pinnacle of German engineering and design.',
    images: [
      'https://images.unsplash.com/photo-1503376780353-7e6692767b70?crop=entropy&cs=srgb&fm=jpg&q=85',
      'https://images.unsplash.com/photo-1611651149918-emnur8a0397f?crop=entropy&cs=srgb&fm=jpg&q=85'
    ],
    price_per_ticket: 1800000,
    total_tickets: 12,
    sold_tickets: 5,
    type: 'Coupe',
    cities: ['Delhi', 'Bangalore', 'Kolkata'],
    features: ['Twin-Turbo Flat-6 - 640 HP', 'PDK Transmission', 'Adaptive Aerodynamics', 'PASM Sport Suspension']
  },
  {
    id: 'range-rover-sv',
    name: 'Range Rover SV Autobiography',
    description: 'The pinnacle of luxury SUVs. Combining off-road capability with supreme luxury. Experience commanding presence and unmatched refinement in this British masterpiece.',
    images: [
      'https://images.unsplash.com/photo-1606664515524-ed2f786a0bd6?crop=entropy&cs=srgb&fm=jpg&q=85',
      'https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?crop=entropy&cs=srgb&fm=jpg&q=85'
    ],
    price_per_ticket: 3500000,
    total_tickets: 8,
    sold_tickets: 2,
    type: 'SUV',
    cities: ['Delhi', 'Mumbai', 'Jaipur'],
    features: ['5.0L Supercharged V8 - 565 HP', 'Terrain Response 2', 'Windsor Leather', 'Meridian Signature Sound']
  },
  {
    id: 'mercedes-s-class',
    name: 'Mercedes-Benz S-Class',
    description: 'The benchmark for luxury sedans. The S-Class combines cutting-edge technology with timeless elegance. Experience the pinnacle of automotive luxury and innovation.',
    images: [
      'https://images.unsplash.com/photo-1618843479313-40f8afb4b4d8?crop=entropy&cs=srgb&fm=jpg&q=85',
      'https://images.unsplash.com/photo-1606016159991-8977ad2a22a9?crop=entropy&cs=srgb&fm=jpg&q=85'
    ],
    price_per_ticket: 1500000,
    total_tickets: 12,
    sold_tickets: 8,
    type: 'Limousine',
    cities: ['Chennai', 'Hyderabad', 'Bangalore'],
    features: ['3.0L Inline-6 Turbo - 429 HP', 'MBUX with AR Navigation', 'Energizing Comfort', 'E-Active Body Control']
  }
];

// Articles for Coming Soon sections
const jetsArticles = [
  {
    title: 'The Future of Private Aviation',
    description: 'Discover how fractional jet ownership is revolutionizing luxury travel.',
    url: '#'
  },
  {
    title: 'Top 10 Private Jets in 2025',
    description: 'Explore the most exclusive private jets available for fractional ownership.',
    url: '#'
  },
  {
    title: 'Benefits of Fractional Jet Ownership',
    description: 'Learn why fractional ownership makes sense for discerning travelers.',
    url: '#'
  }
];

const homesArticles = [
  {
    title: 'Luxury Vacation Homes Worth Investing In',
    description: 'Explore premium properties in the world\'s most desirable destinations.',
    url: '#'
  },
  {
    title: 'The Rise of Fractional Real Estate',
    description: 'How fractional ownership is making luxury properties accessible.',
    url: '#'
  },
  {
    title: 'Managing Your Vacation Home Investment',
    description: 'Expert tips on maximizing your fractional property investment.',
    url: '#'
  }
];

// Major Indian cities
const indianCities = [
  'All India',
  'Mumbai',
  'Delhi',
  'Bangalore',
  'Hyderabad',
  'Ahmedabad',
  'Chennai',
  'Kolkata',
  'Pune',
  'Jaipur',
  'Surat',
  'Lucknow',
  'Kanpur',
  'Nagpur',
  'Indore',
  'Thane',
  'Bhopal',
  'Visakhapatnam',
  'Vadodara',
  'Ludhiana',
  'Agra',
  'Nashik',
  'Faridabad',
  'Meerut',
  'Rajkot',
  'Varanasi',
  'Srinagar',
  'Amritsar',
  'Allahabad',
  'Ranchi',
  'Coimbatore',
  'Chandigarh',
  'Guwahati',
  'Gurgaon',
  'Noida'
];

const HighAssetsPage = ({ user }) => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [showAvailableVehicles, setShowAvailableVehicles] = useState(true);
  const [showCuration, setShowCuration] = useState(true);
  const { cartCount } = useCartCount(user);
  
  // Submenu state for Explore Now button
  const [showExploreMenu, setShowExploreMenu] = useState(null); // null or category id
  
  // Sort & Filter states
  const [sortBy, setSortBy] = useState('');
  const [filterType, setFilterType] = useState('All');
  const [filterCity, setFilterCity] = useState('All India');
  const [visibleCount, setVisibleCount] = useState(4);
  const [userCity, setUserCity] = useState('All India');
  const [showSortFilter, setShowSortFilter] = useState(false);
  
  // API data states
  const [luxuryCars, setLuxuryCars] = useState([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [curationPackages, setCurationPackages] = useState([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://dashboard-flow-5.preview.emergentagent.com';
  
  // Fetch assets from API
  useEffect(() => {
    if (selectedCategory === 'luxury-cars') {
      fetchAssets();
      fetchCurationPackages();
    }
  }, [selectedCategory]);
  
  const fetchCurationPackages = async () => {
    setLoadingPackages(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/curation-packages`);
      if (response.ok) {
        const packages = await response.json();
        const transformedPackages = packages.map(pkg => ({
          id: pkg.slug || pkg.id,
          name: pkg.name,
          price: pkg.complete_package_price || pkg.price_per_day * 360 || 0,
          description: pkg.description,
          image: pkg.package_image_url || pkg.image_url || 'https://via.placeholder.com/400x300?text=Package+Image'
        }));
        setCurationPackages(transformedPackages);
      } else {
        // Fallback to hardcoded packages
        setCurationPackages(getHardcodedPackages());
      }
    } catch (error) {
      console.error('Error fetching curation packages:', error);
      // Fallback to hardcoded packages
      setCurationPackages(getHardcodedPackages());
    } finally {
      setLoadingPackages(false);
    }
  };
  
  const getHardcodedPackages = () => {
    return [
      {
        id: 'urban',
        name: 'Urban',
        price: 10000000,
        description: 'The Urban collection is designed for the modern achiever — refined, dynamic, and always on the move. Your perfect companion for weekday commutes, impression-making business meetings, and spontaneous weekend drives that turn heads wherever you go. Experience sophistication in motion.',
        image: 'https://customer-assets.emergentagent.com/job_luxuryhub/artifacts/bs83r5h0_Urban%202.jpg'
      },
      {
        id: 'grand',
        name: 'Grand',
        price: 20000000,
        description: 'The Grand collection blends comfort, capability, and charisma. Glide through city life in elegance, conquer the open roads in supreme confidence, and unleash pure exhilaration when the weekend arrives. Where every journey becomes a grand experience.',
        image: 'https://customer-assets.emergentagent.com/job_luxuryhub/artifacts/u1fnrxq5_Grand%203.jpg'
      },
      {
        id: 'exotique',
        name: 'Exotique',
        price: 30000000,
        description: "The Exotique collection defines the pinnacle of curated luxury. Command presence, indulge in opulence, and celebrate your love for the extraordinary — whether it's the grace of a Maybach, the might of a Range Rover, or the thrill of an Aston Martin. For those who don't follow luxury — they define it.",
        image: 'https://customer-assets.emergentagent.com/job_luxuryhub/artifacts/olh0gsmz_Exotic%204.jpg'
      }
    ];
  };
  
  const fetchAssets = async () => {
    try {
      setLoadingAssets(true);
      const response = await getAssets('vehicle');
      
      // Ensure response has data property and it's an array
      if (!response || !response.data || !Array.isArray(response.data)) {
        console.warn('Assets response is not in expected format:', response);
        setLuxuryCars([]);
        return;
      }
      
      // Transform API data to match component structure
      const transformedAssets = response.data
        .filter(asset => asset.status === 'active') // Only show active assets
        .map(asset => ({
          id: asset.id,
          name: asset.name,
          description: asset.description || '',
          images: asset.images || [],
          price_per_ticket: asset.price_per_ticket || 0,
          total_tickets: asset.total_tickets || 0,
          sold_tickets: asset.sold_tickets || 0,
          type: asset.category || 'Vehicle',
          cities: asset.cities || [], // NEW: Use cities array
          body_types: asset.body_types || [], // NEW: Use body_types array
          features: asset.features || []
        }));
      setLuxuryCars(transformedAssets);
    } catch (error) {
      console.error('Failed to fetch assets:', error);
      toast.error('Failed to load vehicles. Using cached data if available.');
      // Don't clear luxuryCars - keep any existing data
      if (!Array.isArray(luxuryCars) || luxuryCars.length === 0) {
        setLuxuryCars([]);
      }
    } finally {
      setLoadingAssets(false);
    }
  };
  
  // Restore state when coming back to this page
  useEffect(() => {
    const savedState = sessionStorage.getItem('highAssetsPageState');
    if (savedState) {
      try {
        const state = JSON.parse(savedState);
        setSelectedCategory(state.selectedCategory || null);
        setSortBy(state.sortBy || '');
        setFilterType(state.filterType || 'All');
        setFilterCity(state.filterCity || 'All India');
        setVisibleCount(state.visibleCount || 4);
        setShowSortFilter(state.showSortFilter || false);
        
        // Restore scroll position after a brief delay
        setTimeout(() => {
          if (state.scrollY) {
            window.scrollTo(0, state.scrollY);
          }
        }, 100);
        
        // Clear the saved state after restoring
        sessionStorage.removeItem('highAssetsPageState');
      } catch (error) {
        console.error('Error restoring state:', error);
      }
    }
  }, []);
  
  // Save state before navigating away (when user clicks on asset)
  const savePageState = () => {
    const state = {
      selectedCategory,
      sortBy,
      filterType,
      filterCity,
      visibleCount,
      showSortFilter,
      scrollY: window.scrollY
    };
    sessionStorage.setItem('highAssetsPageState', JSON.stringify(state));
  };
  
  // Scroll to section functions
  const scrollToIndividualCars = () => {
    const element = document.getElementById('individual-vehicles-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setShowExploreMenu(null);
  };
  
  const scrollToCurationPackages = () => {
    const element = document.getElementById('curation-packages-section');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    setShowExploreMenu(null);
  };
  
  // Detect user location on component mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            // Use reverse geocoding to get city (simplified - in production use a proper geocoding service)
            const response = await fetch(
              `https://nominatim.openstreetmap.org/reverse?lat=${position.coords.latitude}&lon=${position.coords.longitude}&format=json`
            );
            const data = await response.json();
            const detectedCity = data.address?.city || data.address?.town || data.address?.state_district;
            
            // Check if detected city is in our list
            const matchedCity = indianCities.find(city => 
              city.toLowerCase().includes(detectedCity?.toLowerCase())
            );
            
            if (matchedCity && matchedCity !== 'All India') {
              setFilterCity(matchedCity);
              setUserCity(matchedCity);
            }
          } catch (error) {
            console.log('Could not detect city');
          }
        },
        (error) => {
          console.log('Geolocation not available');
        }
      );
    }
  }, []);

  const categories = [
    {
      id: 'luxury-cars',
      title: 'Luxury & Exotic Cars',
      description: 'Ultra-Premium Vehicles',
      image: 'https://images.unsplash.com/photo-1597687228894-111db66403b6?crop=entropy&cs=srgb&fm=jpg&q=85',
      available: true
    },
    {
      id: 'private-jets',
      title: 'Private Jets & Helicopters',
      description: 'Exclusive Aviation',
      image: 'https://images.unsplash.com/photo-1540962351504-03099e0a754b?crop=entropy&cs=srgb&fm=jpg&q=85',
      available: false
    },
    {
      id: 'vacation-homes',
      title: 'Vacation Homes',
      description: 'Luxury Properties',
      image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?crop=entropy&cs=srgb&fm=jpg&q=85',
      available: false
    }
  ];

  // Track touch start position and time to distinguish tap from scroll/hold
  const [touchStartY, setTouchStartY] = useState(null);
  const [touchStartTime, setTouchStartTime] = useState(null);
  
  const handleCategoryTouchStart = (e) => {
    setTouchStartY(e.touches[0].clientY);
    setTouchStartTime(Date.now());
  };
  
  const handleCategoryTouchEnd = (categoryId, e) => {
    // Prevent default to avoid click event firing twice
    e.preventDefault();
    
    if (touchStartY === null || touchStartTime === null) {
      setTouchStartY(null);
      setTouchStartTime(null);
      return;
    }
    
    const touchEndY = e.changedTouches[0].clientY;
    const touchDuration = Date.now() - touchStartTime;
    const scrollDistance = Math.abs(touchEndY - touchStartY);
    
    // If user scrolled more than 10px OR held for more than 500ms, don't trigger click
    if (scrollDistance > 10 || touchDuration > 500) {
      setTouchStartY(null);
      setTouchStartTime(null);
      return;
    }
    
    // Valid tap detected - trigger category selection
    handleCategorySelection(categoryId);
    setTouchStartY(null);
    setTouchStartTime(null);
  };
  
  const handleCategoryClick = (categoryId) => {
    // Handle regular mouse clicks (desktop)
    handleCategorySelection(categoryId);
  };
  
  const handleCategorySelection = (categoryId) => {
    setSelectedCategory(categoryId);
    // Reset filters when changing category
    setVisibleCount(4);
    setSortBy('');
    setFilterType('All');
    setFilterCity(userCity);
    
    // Scroll to the content section below with enhanced visibility
    setTimeout(() => {
      const contentSection = document.getElementById('category-content');
      if (contentSection) {
        // Calculate position to scroll - add offset to ensure content is clearly visible
        const yOffset = -100; // 100px above the content to give breathing room
        const element = contentSection;
        const y = element.getBoundingClientRect().top + window.pageYOffset + yOffset;
        
        window.scrollTo({
          top: y,
          behavior: 'smooth'
        });
      }
    }, 150);
  };
  
  // Apply filters and sorting
  const getFilteredAndSortedCars = () => {
    let filtered = [...luxuryCars]; // Use API data instead of mock
    
    // Apply body type filter (NEW: using body_types array)
    if (filterType !== 'All') {
      filtered = filtered.filter(car => car.body_types?.includes(filterType));
    }
    
    // Apply city filter (NEW: using cities array)
    if (filterCity !== 'All India') {
      filtered = filtered.filter(car => car.cities?.includes(filterCity));
    }
    
    // Apply sorting
    switch(sortBy) {
      case 'price-high':
        filtered.sort((a, b) => b.price_per_ticket - a.price_per_ticket);
        break;
      case 'price-low':
        filtered.sort((a, b) => a.price_per_ticket - b.price_per_ticket);
        break;
      case 'tickets-high':
        filtered.sort((a, b) => b.sold_tickets - a.sold_tickets);
        break;
      case 'tickets-low':
        filtered.sort((a, b) => a.sold_tickets - b.sold_tickets);
        break;
      default:
        break;
    }
    
    return filtered;
  };
  
  // Get unique cities from all assets (NEW: Dynamic filter options)
  const getUniqueCities = () => {
    if (!Array.isArray(luxuryCars) || luxuryCars.length === 0) {
      return ['All India'];
    }
    const allCities = luxuryCars.flatMap(car => car.cities || []);
    const uniqueCities = [...new Set(allCities)].filter(city => city).sort();
    return ['All India', ...uniqueCities];
  };
  
  // Get unique body types from all assets (NEW: Dynamic filter options)
  const getUniqueBodyTypes = () => {
    if (!Array.isArray(luxuryCars) || luxuryCars.length === 0) {
      return ['All'];
    }
    const allBodyTypes = luxuryCars.flatMap(car => car.body_types || []);
    const uniqueBodyTypes = [...new Set(allBodyTypes)].filter(type => type).sort();
    return ['All', ...uniqueBodyTypes];
  };
  
  const handleLoadMore = () => {
    setVisibleCount(prev => prev + 4);
  };

  // Curation packages are now fetched from API via fetchCurationPackages()

  const renderAssets = () => {
    if (selectedCategory === 'luxury-cars') {
      // Show loading state while fetching
      if (loadingAssets) {
        return <LoadingSpinner size="lg" message="Loading vehicles..." />;
      }
      
      const filteredCars = getFilteredAndSortedCars();
      const displayedCars = filteredCars.slice(0, visibleCount);
      const hasMore = visibleCount < filteredCars.length;
      
      return (
        <div id="category-content" className="mt-12 space-y-16">
          {/* 360 Days Curation Section */}
          <div id="curation-packages-section">
            <div className="flex items-center justify-between mb-4 cursor-pointer" onClick={() => setShowCuration(!showCuration)}>
              <h2 className="text-4xl font-bold">360 Days Curation</h2>
              <ChevronRight className={`w-8 h-8 transition-transform ${showCuration ? 'rotate-90' : ''}`} />
            </div>
            {showCuration && (
              <>
                <p className="text-lg text-gray mb-8">
                  The curation consists of 3 best cars carefully curated into 3 segments: Commuter, Outstation, & Weekender. 
                  The curation package ensures you have either of the 3 cars on every given day of the year. 3 cars for 360 days.
                </p>
                {loadingPackages ? (
                  <LoadingSpinner size="lg" message="Loading curation packages..." />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
                    {curationPackages.map((pkg) => (
                    <div key={pkg.id} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow flex flex-col">
                      <ImageWithLoader src={pkg.image} alt={pkg.name} className="w-full h-40 sm:h-48 object-cover" placeholderColor="#f3f4f6" />
                      <div className="p-4 sm:p-6 md:p-8 flex flex-col flex-grow">
                        <div className="flex-grow">
                          <h3 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 uppercase tracking-wide">{pkg.name}</h3>
                          <p className="text-gray mb-4 sm:mb-6 text-sm sm:text-base">{pkg.description}</p>
                        </div>
                        <div className="mt-auto">
                          <div className="mb-4 sm:mb-6">
                            <p className="text-xs sm:text-sm text-gray mb-1">Complete Package Starting from</p>
                            <span className="text-gold text-xl sm:text-2xl font-bold">₹{pkg.price.toLocaleString('en-IN')}</span>
                          </div>
                          <Link to={`/curation/${pkg.id}`} onClick={savePageState}>
                            <Button className="w-full bg-black text-white hover:bg-black/90 text-sm sm:text-base py-2 sm:py-3">View Package</Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>

          {/* Individual Vehicles Section */}
          <div id="individual-vehicles-section" className="border-t-2 border-gray-200 pt-16">
            <div className="flex items-center justify-between mb-4 cursor-pointer" onClick={() => setShowAvailableVehicles(!showAvailableVehicles)}>
              <h2 className="text-4xl font-bold">Individual Vehicles</h2>
              <ChevronRight className={`w-8 h-8 transition-transform ${showAvailableVehicles ? 'rotate-90' : ''}`} />
            </div>
            {showAvailableVehicles && (
              <>
                <p className="text-lg text-gray mb-8">
                  You may choose to fractionally invest in any of the individual vehicles. 
                  Each ticket denotes 30 days of usage per year.
                </p>
                
                {/* Sort & Filter Toggle Button */}
                <div className="mb-6">
                  <button
                    onClick={() => setShowSortFilter(!showSortFilter)}
                    className="flex items-center text-lg font-semibold hover:text-gold transition-colors"
                  >
                    <span>Sort/Filter</span>
                    <ChevronRight className={`w-6 h-6 ml-2 transition-transform ${showSortFilter ? 'rotate-90' : ''}`} />
                  </button>
                </div>
                
                {/* Sort & Filter Controls */}
                {showSortFilter && (
                  <div className="bg-white rounded-lg p-6 mb-8 shadow-lg">
                    <div className="grid md:grid-cols-3 gap-6">
                      {/* Sort By */}
                      <div>
                        <label className="block text-xs font-bold mb-2 uppercase tracking-widest">
                          Sort By
                        </label>
                        <div className="relative">
                          <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="w-full px-3 py-3 sm:py-2 text-sm bg-white border border-gray-200 rounded appearance-none cursor-pointer focus:outline-none focus:border-gold focus:shadow-md transition-all font-medium min-h-[44px]"
                            style={{ backgroundImage: 'none' }}
                          >
                            <option value="">Default</option>
                            <option value="price-high">Price: High to Low</option>
                            <option value="price-low">Price: Low to High</option>
                            <option value="tickets-high">Tickets Sold: High to Low</option>
                            <option value="tickets-low">Tickets Sold: Low to High</option>
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-black">
                            <ChevronRight className="w-4 h-4 rotate-90" />
                          </div>
                        </div>
                      </div>
                      
                      {/* Filter by Type */}
                      <div>
                        <label className="block text-xs font-bold mb-2 uppercase tracking-widest">
                          Car Type
                        </label>
                        <div className="relative">
                          <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="w-full px-3 py-3 sm:py-2 text-sm bg-white border border-gray-200 rounded appearance-none cursor-pointer focus:outline-none focus:border-gold focus:shadow-md transition-all font-medium min-h-[44px]"
                            style={{ backgroundImage: 'none' }}
                          >
                            {getUniqueBodyTypes().map(type => (
                              <option key={type} value={type}>
                                {type === 'All' ? 'All Types' : type}
                              </option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-black">
                            <ChevronRight className="w-4 h-4 rotate-90" />
                          </div>
                        </div>
                      </div>
                      
                      {/* Filter by City */}
                      <div>
                        <label className="block text-xs font-bold mb-2 uppercase tracking-widest">
                          City
                        </label>
                        <div className="relative">
                          <select
                            value={filterCity}
                            onChange={(e) => setFilterCity(e.target.value)}
                            className="w-full px-3 py-3 sm:py-2 text-sm bg-white border border-gray-200 rounded appearance-none cursor-pointer focus:outline-none focus:border-gold focus:shadow-md transition-all font-medium min-h-[44px]"
                            style={{ backgroundImage: 'none' }}
                          >
                            {getUniqueCities().map(city => (
                              <option key={city} value={city}>{city}</option>
                            ))}
                          </select>
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-black">
                            <ChevronRight className="w-4 h-4 rotate-90" />
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Results count */}
                    <div className="mt-4 pt-4 text-xs font-semibold uppercase tracking-wide text-gray">
                      Showing {displayedCars.length} of {filteredCars.length} vehicles
                      {filterCity !== 'All India' && ` in ${filterCity}`}
                    </div>
                  </div>
                )}
                
                {/* Cars Grid */}
                {filteredCars.length === 0 ? (
                  <div className="text-center py-12 bg-white rounded-lg">
                    <p className="text-xl text-gray">No vehicles match your filters. Try adjusting your criteria.</p>
                  </div>
                ) : (
                  <>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
                      {displayedCars.map((asset) => (
                        <div key={asset.id} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-2xl transition-shadow">
                          <ImageWithLoader src={asset.images[0]} alt={asset.name} className="w-full h-48 sm:h-56 md:h-64 object-cover" placeholderColor="#f3f4f6" />
                          <div className="p-4 sm:p-6">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-2xl font-bold">{asset.name}</h3>
                              <span className="text-xs bg-gold text-black px-2 py-1 rounded uppercase font-semibold">{asset.type}</span>
                            </div>
                            <p className="text-gray mb-2 text-sm">Available in: {asset.cities.join(', ')}</p>
                            <p className="text-gray mb-4">{asset.description.substring(0, 100)}...</p>
                            <div className="mb-4">
                              <div className="flex justify-between text-sm mb-2">
                                <span>Tickets Sold</span>
                                <span className="font-semibold">{asset.sold_tickets}/{asset.total_tickets}</span>
                              </div>
                              <Progress value={(asset.sold_tickets / asset.total_tickets) * 100} className="h-2" />
                            </div>
                            <div className="flex items-center justify-between">
                              <div>
                                <span className="text-gold text-2xl font-bold">₹{asset.price_per_ticket.toLocaleString()}</span>
                                <span className="text-sm text-gray"> /ticket</span>
                              </div>
                              <Link to={`/asset/${asset.id}`} onClick={savePageState}>
                                <Button className="bg-black text-white hover:bg-black/90">View Details</Button>
                              </Link>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Load More Button */}
                    {hasMore && (
                      <div className="text-center mt-8">
                        <Button 
                          onClick={handleLoadMore}
                          className="bg-white text-gold hover:bg-white/90 px-8 py-6 text-lg shadow-lg"
                        >
                          Load More Vehicles
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      );
    }

    if (selectedCategory === 'private-jets') {
      return (
        <div id="category-content" className="mt-12">
          <div className="bg-white rounded-lg p-12 shadow-lg text-center">
            <h2 className="text-4xl font-bold mb-4">Private Jets & Helicopters</h2>
            <p className="text-2xl text-gold mb-8">Coming Soon</p>
            <p className="text-gray mb-12 max-w-2xl mx-auto">
              We're curating an exclusive collection of private jets and helicopters for fractional ownership. 
              Experience the ultimate in luxury travel. Stay tuned for updates.
            </p>
          </div>
        </div>
      );
    }

    if (selectedCategory === 'vacation-homes') {
      return (
        <div id="category-content" className="mt-12">
          <div className="bg-white rounded-lg p-12 shadow-lg text-center">
            <h2 className="text-4xl font-bold mb-4">Luxury Vacation Homes</h2>
            <p className="text-2xl text-gold mb-8">Coming Soon</p>
            <p className="text-gray mb-12 max-w-2xl mx-auto">
              We're handpicking the world's most exclusive vacation properties for fractional ownership. 
              Experience luxury living in premier destinations. Stay tuned for updates.
            </p>
          </div>
        </div>
      );
    }

    // Default: Show call to action when no category is selected
    return (
      <div className="mt-12 text-center bg-black text-white rounded-lg p-6 sm:p-8 md:p-12">
        <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Ready to Start Your Journey?</h2>
        <p className="text-base sm:text-lg mb-6 sm:mb-8 text-white/80">Select a category above to explore available assets</p>
        <Link to="/register-interest">
          <Button className="bg-white text-gold hover:bg-white/90 px-6 sm:px-8 py-4 sm:py-6 text-base sm:text-lg font-bold shadow-lg w-full sm:w-auto">
            Express Your Interest
          </Button>
        </Link>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-cream">
      <NavigationBar user={user} cartCount={cartCount} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-12 md:py-16 lg:py-20">
        {/* Header - Always Visible */}
        <div className="text-center mb-8 sm:mb-12 md:mb-16">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 sm:mb-4">
            High Assets
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray px-4 mb-3">
            Fractional ownership of the world's most exclusive assets
          </p>
          <p className="text-lg sm:text-xl md:text-2xl font-bold px-4" style={{ color: '#D4AF37' }}>
            Officially Launching in January 2026
          </p>
        </div>

        {/* Category Cards - Always Visible */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 mb-8 sm:mb-12">
          {categories.map((category) => (
            <div
              key={category.id}
              onTouchStart={handleCategoryTouchStart}
              onTouchEnd={(e) => handleCategoryTouchEnd(category.id, e)}
              onClick={() => handleCategoryClick(category.id)}
              className={`cursor-pointer group transition-all ${
                selectedCategory === category.id ? 'ring-4 ring-gold' : ''
              }`}
            >
              <div className="bg-white overflow-hidden hover:shadow-2xl transition-shadow h-full flex flex-col shadow-md border border-gray-200">
                <img 
                  src={category.image} 
                  alt={category.title} 
                  className="w-full h-48 sm:h-56 md:h-64 lg:h-72 object-cover"
                />
                <div className="p-4 sm:p-6 md:p-8 text-center border-t-4 border-black flex-grow flex flex-col justify-between">
                  <div>
                    <h3 className="text-lg sm:text-xl md:text-2xl font-bold mb-3 sm:mb-4 uppercase tracking-wide">{category.title}</h3>
                    <p className="text-gray text-xs sm:text-sm mb-3 sm:mb-4">{category.description}</p>
                  </div>
                  
                  {/* Explore Now Button with Submenu */}
                  {category.available ? (
                    <div className="relative">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowExploreMenu(showExploreMenu === category.id ? null : category.id);
                        }}
                        className="inline-flex items-center justify-center mt-3 sm:mt-4 text-gold text-xs sm:text-sm uppercase font-semibold hover:text-amber-600 transition-colors"
                      >
                        Explore Now
                        <ChevronRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1" />
                      </button>
                      
                      {/* Submenu */}
                      {showExploreMenu === category.id && (
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 bg-white border-2 border-gold rounded-lg shadow-xl z-10 w-56 animate-in fade-in slide-in-from-bottom-2 duration-200">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCategorySelection(category.id);
                              setTimeout(scrollToIndividualCars, 300);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-cream transition-colors text-gray-800 font-semibold text-sm border-b border-gray-200"
                          >
                            Individual Cars
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCategorySelection(category.id);
                              setShowCuration(true); // Open the curation section
                              setTimeout(scrollToCurationPackages, 300);
                            }}
                            className="w-full px-4 py-3 text-left hover:bg-cream transition-colors text-gray-800 font-semibold text-sm"
                          >
                            360 Days Curation
                          </button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="inline-flex items-center justify-center mt-3 sm:mt-4 text-gold text-xs sm:text-sm uppercase font-semibold">
                      Coming Soon
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Assets Display Section - Always renders something */}
        {renderAssets()}
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HighAssetsPage;
